import type { Answers, ItemStates } from "@features/session/hooks/types";
import { isAnswered } from "@memorang/helpers/item";
import type { BaseItemSchema } from "@memorang/types";
import type { QuestionMapItemState } from "@memorang/ui/components/QuestionMapItem";
import type {
	SessionItemsNavFilterValue,
	SessionItemsNavItem,
	SessionItemsNavItemStatus,
} from "@memorang/ui/components/SessionItemsNavPane";

type BuildSessionItemsNavItemsParams = {
	items: BaseItemSchema[];
	itemStates?: ItemStates;
	answers?: Answers;
	markedItemIds?: number[];
	inReviewMode?: boolean;
	itemViewedTimestamps?: Map<number, string>;
	allowMarking?: boolean;
};

const getItemLabel = (index: number) => {
	return String(index + 1);
};

const getSelectedChoiceIds = (
	item: BaseItemSchema,
	itemStates?: ItemStates,
	answers?: Answers,
) => {
	const itemId = item.id;
	if (itemId == null) {
		return [];
	}

	return (
		itemStates?.get(itemId)?.selectedChoiceIds ||
		answers?.get(itemId)?.selectedChoiceIds ||
		item.answer?.selectedChoiceIds ||
		[]
	);
};

const getIsCorrect = (
	item: BaseItemSchema,
	itemStates?: ItemStates,
	answers?: Answers,
) => {
	const itemId = item.id;
	if (itemId == null) {
		return false;
	}

	const persistedCorrect =
		answers?.get(itemId)?.correct ?? item.answer?.correct ?? null;
	if (persistedCorrect != null) {
		return persistedCorrect;
	}

	const selectedChoiceIds = getSelectedChoiceIds(item, itemStates, answers);
	const response = item.response;
	if (response.type !== "choice" || !selectedChoiceIds.length) {
		return false;
	}

	if (response.selectionType === "multiple") {
		const correctChoiceIds = response.choices
			.filter((choice) => choice.isCorrect)
			.map((choice) => choice.id);

		return (
			selectedChoiceIds.length === correctChoiceIds.length &&
			selectedChoiceIds.every((choiceId) => correctChoiceIds.includes(choiceId))
		);
	}

	const correctChoiceId = response.choices.find(
		(choice) => choice.isCorrect,
	)?.id;
	return selectedChoiceIds[0] === correctChoiceId;
};

export const getHasResponse = (
	item: BaseItemSchema,
	itemStates?: ItemStates,
	answers?: Answers,
) => {
	const itemId = item.id;
	if (itemId == null) {
		return false;
	}

	const selectedChoiceIds = getSelectedChoiceIds(item, itemStates, answers);

	return (
		selectedChoiceIds.length > 0 ||
		Boolean(itemStates?.get(itemId)?.answered) ||
		isAnswered(answers?.get(itemId)) ||
		isAnswered(item.answer)
	);
};

const getWasReached = (
	item: BaseItemSchema,
	answers?: Answers,
	itemViewedTimestamps?: Map<number, string>,
) => {
	// A persisted skip still carries interaction fields (timeTaken); the
	// backend also fabricates correct:false stubs for never-seen items, so
	// bare record existence is not evidence the user reached the item.
	return (
		item.answer?.timeTaken != null ||
		(item.id != null &&
			(itemViewedTimestamps?.has(item.id) || answers?.get(item.id) != null))
	);
};

export const getItemStatus = ({
	item,
	itemStates,
	answers,
	inReviewMode,
	itemViewedTimestamps,
}: {
	item: BaseItemSchema;
	itemStates?: ItemStates;
	answers?: Answers;
	inReviewMode?: boolean;
	itemViewedTimestamps?: Map<number, string>;
}): SessionItemsNavItemStatus => {
	const hasResponse = getHasResponse(item, itemStates, answers);

	if (!hasResponse) {
		if (inReviewMode) {
			return "skipped";
		}
		return getWasReached(item, answers, itemViewedTimestamps)
			? "skipped"
			: "unanswered";
	}

	if (!inReviewMode) {
		return "answered";
	}

	return getIsCorrect(item, itemStates, answers) ? "correct" : "incorrect";
};

export const getQuestionMapItemState = ({
	item,
	itemStates,
	answers,
	inReviewMode,
	currentItemId,
	itemViewedTimestamps,
}: {
	item: BaseItemSchema;
	itemStates?: ItemStates;
	answers?: Answers;
	inReviewMode?: boolean;
	currentItemId?: number;
	itemViewedTimestamps?: Map<number, string>;
}): QuestionMapItemState => {
	if (item.id === currentItemId) {
		return "current";
	}

	const status = getItemStatus({
		item,
		itemStates,
		answers,
		inReviewMode,
		itemViewedTimestamps,
	});

	switch (status) {
		case "correct":
			return "correct";
		case "incorrect":
			return "incorrect";
		case "skipped":
			return "skipped";
		case "answered":
			return "answered";
		case "unanswered":
			return "notVisited";
		default:
			return "notVisited";
	}
};

export const getSessionItemsNavFiltersForMode = (
	inReviewMode?: boolean,
	allowMarking = true,
): SessionItemsNavFilterValue[] => {
	const filters: SessionItemsNavFilterValue[] = inReviewMode
		? ["correct", "incorrect", "skipped", "marked"]
		: ["answered", "unanswered", "skipped", "marked"];

	return allowMarking
		? filters
		: filters.filter((filter) => filter !== "marked");
};

export const buildSessionItemsNavItems = ({
	items,
	itemStates,
	answers,
	markedItemIds,
	inReviewMode,
	itemViewedTimestamps,
	allowMarking = true,
}: BuildSessionItemsNavItemsParams): SessionItemsNavItem[] => {
	return items.map((item, index) => {
		const status = getItemStatus({
			item,
			itemStates,
			answers,
			inReviewMode,
			itemViewedTimestamps,
		});

		return {
			id: String(item.id),
			label: getItemLabel(index),
			status,
			isMarked:
				allowMarking &&
				(Boolean(item.answer?.marked) ||
					Boolean(markedItemIds?.includes(item.id ?? -1))),
			disabled: !inReviewMode && status === "unanswered",
		};
	});
};

export const getSessionItemsNavProgress = (
	targetIndex: number,
	totalItems: number,
) => {
	if (totalItems <= 0) {
		return 0;
	}

	return (targetIndex + 1) / totalItems;
};
